import { Panel, PanelConfig, Door, CeilingPanel, PanelEndType, Hari } from '../types';

/**
 * 주어진 길이를 패널로 채우는 알고리즘
 * 표준 패널(900mm)로 최대한 채우고 남는 부분은 커스텀 사이즈
 */
function fillWithPanels(length: number, config: PanelConfig): number[] {
  const defaultSize = config.DEFAULT_SIZE; // 900
  const halfSize = config.HALF_SIZE ?? 441;      // 반절 패널 (재질 규격)
  const minSize = config.SPLIT_MIN_SIZE ?? 200;  // 이보다 작으면 안됨 (재질 규격)
  const panelSpace = config.PANEL_SPACE;   // 4

  if (length <= 0) return [];
  if (length < minSize + panelSpace) return [minSize]; // 최소 사이즈 보장
  if (length <= defaultSize + panelSpace) return [length - panelSpace]; // 1장이어도 간격 4 (이전 시스템 실제 기록 기준)

  // 1) 900으로 최대한 채우기 (각 패널마다 panelSpace 적용)
  const sizes: number[] = [];
  let remaining = length;
  while (remaining > defaultSize + panelSpace) {
    sizes.push(defaultSize);
    remaining -= defaultSize + panelSpace;
  }
  // 마지막 패널도 panelSpace 차감
  remaining -= panelSpace;

  // 2) 나머지가 minSize(200) 이상이면 그대로 마지막 패널
  if (remaining >= minSize) {
    sizes.push(remaining);
    return sizes;
  }

  // 3) 나머지가 200 미만 → 마지막 900을 441로 교체, 나머지 재계산
  if (sizes.length > 0) {
    sizes[sizes.length - 1] = halfSize;
    remaining += (defaultSize - halfSize); // 441로 줄인 만큼 나머지에 추가
    if (remaining > defaultSize) {
      // 나머지가 900 초과 → 900 + 나머지로 분할
      sizes.push(defaultSize);
      remaining -= defaultSize + panelSpace;
    }
    if (remaining > 0) {
      sizes.push(remaining);
    }
  } else {
    sizes.push(remaining);
  }

  return sizes;
}

/**
 * 벽면 패널 배치 (문 포함)
 * 문이 있으면 문 영역을 제외한 앞/뒤 구간에 패널을 배치
 */
export function calculatePanelLayout(
  wallLength: number,
  config: PanelConfig,
  wallSide: 'left' | 'right' | 'top' | 'bottom',
  doors?: Door[]
): Panel[] {
  const panelSpace = config.PANEL_SPACE;
  const isVerticalWall = wallSide === 'left' || wallSide === 'right';

  // 해당 벽의 문들을 위치 순으로 정렬
  const wallDoors = (doors || [])
    .filter(d => d.wallSide === wallSide)
    .sort((a, b) => a.position - b.position);

  if (wallDoors.length === 0) {
    const sizes = fillWithPanels(wallLength, config);
    return sizesToPanels(sizes, 0, config, wallSide, isVerticalWall, 0, true, true);
  }

  // 문이 있는 경우: 구간별로 패널 채움
  const panels: Panel[] = [];
  let currentPos = 0;
  let panelIndex = 0;

  for (let di = 0; di < wallDoors.length; di++) {
    const door = wallDoors[di];
    const doorTotal = door.leftPanel + door.width + door.rightPanel;
    const isFirstSegment = di === 0 && currentPos < 1;

    const beforeLen = door.position - currentPos;
    if (beforeLen > panelSpace) {
      const isHari = door.id.startsWith('hari_');
      const beforeSizes = fillWithPanels(beforeLen, config);
      const beforePanels = sizesToPanels(beforeSizes, currentPos, config, wallSide, isVerticalWall, panelIndex, isFirstSegment, false, !isHari);
      panels.push(...beforePanels);
      panelIndex += beforePanels.length;
    }

    // 하리(가짜 Door)는 패널 간격 없이 바로 붙임
    const isHari = door.id.startsWith('hari_');
    currentPos = door.position + doorTotal + (isHari ? 0 : panelSpace);
  }

  const afterLen = wallLength - currentPos;
  if (afterLen > panelSpace) {
    const afterSizes = fillWithPanels(afterLen, config);
    // 마지막 door/hari가 하리인 경우 isDoorStart=false (삼각형 표시)
    const lastDoor = wallDoors[wallDoors.length - 1];
    const lastIsHari = lastDoor?.id.startsWith('hari_');
    const hasDoor = wallDoors.length > 0 && !lastIsHari;
    const afterPanels = sizesToPanels(afterSizes, currentPos, config, wallSide, isVerticalWall, panelIndex, false, true, false, hasDoor);
    panels.push(...afterPanels);
  }

  return panels;
}

/**
 * 사이즈 배열을 Panel 객체로 변환
 */
function sizesToPanels(
  sizes: number[],
  startPos: number,
  config: PanelConfig,
  wallSide: 'left' | 'right' | 'top' | 'bottom',
  isVerticalWall: boolean,
  startIndex: number = 0,
  isWallStart: boolean = false,
  isWallEnd: boolean = false,
  isDoorEnd: boolean = false,   // 마지막 패널이 문과 맞닿음 → rightType=yangsu
  isDoorStart: boolean = false  // 첫 패널이 문과 맞닿음 → leftType=eumsu
): Panel[] {
  const panels: Panel[] = [];
  let currentPos = startPos;

  sizes.forEach((size, i) => {
    const isFirst = i === 0 && isWallStart;
    const isLast = i === sizes.length - 1 && isWallEnd;
    const isLastPanel = i === sizes.length - 1;
    const isFirstPanel = i === 0;

    // 기본: 양수/음수 (1번 패널)
    let leftType: 'yangsu' | 'eumsu' | 'corner' | 'none' = 'yangsu';
    let rightType: 'yangsu' | 'eumsu' | 'corner' | 'none' = 'eumsu';

    // 코너 패널 설정 (반시계 방향 기준)
    // 좌측벽(↓): 첫 패널의 위쪽(lt)이 상단벽과 코너 연결
    //             마지막 패널의 아래쪽(rt)이 하단벽과 코너 연결
    // 우측벽(↑): 첫 패널의 위쪽(lt)이 상단벽과 코너 연결
    //             마지막 패널의 아래쪽(rt)이 하단벽과 코너 연결
    // 상단/하단벽: 좌/우측벽 두께 영역에 막히므로 코너 패널 없음
    // 반시계 방향 기준 코너 패널 설정:
    // 좌측벽(↓): 첫=위쪽코너(lt=corner), 마지막=아래쪽코너(rt=corner)
    // 우측벽(↑): 첫=아래쪽코너(lt=corner), 마지막=위쪽코너(rt=corner)
    // 상단벽(←): 마지막=오른쪽코너(rt=corner) [우상단]
    // 하단벽(→): 첫=왼쪽코너(lt=corner) [좌하단]
    // PanelTest 확정 규칙:
    // 좌측벽 첫=4번(-90도): lt=yangsu, rt=corner → 회전 후 위=양수, 아래=코너△(상단벽연결)
    // 좌측벽 마지막=1번(-90도): lt=yangsu, rt=eumsu (기본)
    // 우측벽 마지막=4번(-270도): lt=yangsu, rt=corner → 회전 후 위=코너△, 아래=양수
    // 상단벽 마지막=4번(0도): lt=yangsu, rt=corner
    // 하단벽 첫=4번(-180도): lt=yangsu, rt=corner → 회전 후 왼쪽=코너△
    if (wallSide === 'left') {
      if (isFirst) { leftType = 'yangsu'; rightType = 'corner'; }
    }
    if (wallSide === 'right') {
      if (isLast) { leftType = 'yangsu'; rightType = 'corner'; }
    }
    if (wallSide === 'top') {
      if (isLast) { leftType = 'yangsu'; rightType = 'corner'; }
    }
    if (wallSide === 'bottom') {
      if (isFirst) { leftType = 'yangsu'; rightType = 'corner'; }
    }

    // 문과 맞닿는 끝: none (문 사이드패널에서 삼각형을 그리므로 일반 패널은 생략)
    const isReversed = wallSide === 'bottom' || wallSide === 'left';
    if (!isReversed) {
      if (isDoorEnd && isLastPanel)    rightType = 'none';
      if (isDoorStart && isFirstPanel) leftType  = 'none';
    } else {
      if (isDoorEnd && isLastPanel)    leftType  = 'none';
      if (isDoorStart && isFirstPanel) rightType = 'none';
    }

    panels.push({
      x: isVerticalWall ? 0 : currentPos,
      y: isVerticalWall ? currentPos : 0,
      width: isVerticalWall ? 0 : size,
      height: isVerticalWall ? size : 0,
      size,
      isCustom: size !== config.DEFAULT_SIZE,
      wallSide,
      index: startIndex + i,
      grooveDir: 'RIGHT',
      leftType,
      rightType,
    });
    currentPos += size + config.PANEL_SPACE;
  });

  return panels;
}

/**
 * 하리를 해당 벽의 가짜 Door로 변환 (leftPanel=0, rightPanel=0, 사이드패널 없음)
 * calculatePanelLayout의 배제 구간 처리를 재사용하기 위함
 */
function harisToDoors(
  haris: Hari[],
  wallSide: 'left' | 'right' | 'top' | 'bottom',
  horizontal: number,
  vertical: number,
  panelThick: number,
  cornerExclAdjust: Record<string, number> = {}
): Door[] {
  const fakeDoors: Door[] = [];
  const isHoriz = wallSide === 'top' || wallSide === 'bottom';
  const vertLen = vertical - panelThick;
  const horizLen = horizontal - panelThick;

  haris.forEach(hari => {
    if (hari.hariType === 'corner') {
      const c = hari.corner;
      // 상단벽: topLeft→왼쪽 width, topRight→오른쪽 width
      // 하단벽: bottomLeft→왼쪽 width, bottomRight→오른쪽 width
      // 좌측벽: topLeft→위 height, bottomLeft→아래 height
      // 우측벽: topRight→위 height, bottomRight→아래 height
      // 코너형 하리: 패널 로컬좌표는 panelThick 이후부터 시작
      // top/bottom벽 로컬0 = 도면 x=panelThick → 하리 width에서 pt를 뺀 만큼만 배제
      // left/right벽 로컬0 = 도면 y=0 → topLeft/topRight는 그대로, bottomLeft/bottomRight는 vertLen에서 계산
      // 꺾임 체인 규칙: 시계 흐름의 도착(받는) 벽은 기둥 앞(−4mm)까지 와서 관통 — 제외를 두께만큼 축소
      // 상단벽: TL은 출발(왼끝이 기둥 col 다음부터), TR은 도착(벽이 3996까지 와서 양수로 찌름)
      if (wallSide === 'top' && (c === 'topLeft' || c === 'topRight')) {
        const excl = (c === 'topLeft' ? hari.width - panelThick : hari.width) + (cornerExclAdjust[`top:${c}`] ?? 0);
        if (excl <= 0) return;
        const pos = c === 'topLeft' ? 0 : horizLen - excl;
        fakeDoors.push({ id: `hari_${hari.id}`, wallSide, position: pos, width: excl, height: 0, leftPanel: 0, rightPanel: 0, grooveDir: 'NONE' as any, openDir: 'right', doorType: 'single-right' });
      } else if (wallSide === 'bottom' && (c === 'bottomLeft' || c === 'bottomRight')) {
        // BL은 출발(왼끝이 기둥 col 다음부터), BR은 도착(벽이 렌더 확장으로 기둥 col 관통)
        const excl = (c === 'bottomLeft' ? hari.width - panelThick : hari.width) + (cornerExclAdjust[`bottom:${c}`] ?? 0);
        if (excl <= 0) return;
        const pos = c === 'bottomLeft' ? 0 : horizLen - excl;
        fakeDoors.push({ id: `hari_${hari.id}`, wallSide, position: pos, width: excl, height: 0, leftPanel: 0, rightPanel: 0, grooveDir: 'NONE' as any, openDir: 'right', doorType: 'single-right' });
      } else if (wallSide === 'left' && (c === 'topLeft' || c === 'bottomLeft')) {
        // TL은 출발(위끝 양수), BL은 도착(벽이 렌더 확장으로 기둥 row 관통)
        const excl = (c === 'topLeft' ? hari.height : hari.height - panelThick) + (cornerExclAdjust[`left:${c}`] ?? 0);
        const pos = c === 'topLeft' ? 0 : vertLen - excl;
        fakeDoors.push({ id: `hari_${hari.id}`, wallSide, position: pos, width: excl, height: 0, leftPanel: 0, rightPanel: 0, grooveDir: 'NONE' as any, openDir: 'right', doorType: 'single-right' });
      } else if (wallSide === 'right' && (c === 'topRight' || c === 'bottomRight')) {
        // TR은 도착이지만 위로 렌더 확장 관통이라 전체 배제 유지, BR은 아래끝 양수가 수평 관통부를 찌름
        const excl = (c === 'topRight' ? hari.height : hari.height - panelThick) + (cornerExclAdjust[`right:${c}`] ?? 0);
        const pos = c === 'topRight' ? 0 : vertLen - excl;
        fakeDoors.push({ id: `hari_${hari.id}`, wallSide, position: pos, width: excl, height: 0, leftPanel: 0, rightPanel: 0, grooveDir: 'NONE' as any, openDir: 'right', doorType: 'single-right' });
      }
    } else {
      // 중간형: wallSide가 일치하는 경우
      if (hari.wallSide !== wallSide) return;
      // 좌측벽: 하리 팔이 벽 기둥을 관통(팔이 코너 소유) → 제외 구간 = width 전체
      // 나머지: 벽 패널이 팔 칸을 덮음(벽이 코너 소유) → width − PT
      const segLen = wallSide === 'left' ? hari.width : hari.width - panelThick;
      fakeDoors.push({ id: `hari_${hari.id}`, wallSide, position: hari.position ?? 0, width: segLen, height: 0, leftPanel: 0, rightPanel: 0, grooveDir: 'NONE' as any, openDir: 'right', doorType: 'single-right' });
    }
  });

  return fakeDoors;
}

/**
 * 전체 4면 벽의 패널 배치 계산 (문 + 하리 포함)
 */
export function calculateAllWalls(
  horizontal: number,
  vertical: number,
  config: PanelConfig,
  doors?: Door[],
  panelThick?: number,
  haris?: Hari[],
  leftWallExtra: number = 0, // 좌측벽이 좌하단 코너 셀까지 관통할 때 PT — 관통분을 분할에 포함해 900 초과 방지
  // 코너형 하리 제외 폭 보정 { 'top:topRight': -100, ... } — 그리는 방향(CW/CCW)마다 벽 시작점이 달라
  // 벽이 코너 하리 내벽 바깥면에서 정확히 끝나도록 맞춤
  cornerExclAdjust: Record<string, number> = {}
) {
  const pt = panelThick || 100;
  const allHaris = haris || [];

  // 하리를 벽별 가짜 Door로 변환 후 실제 Door와 합침
  const doorsWithHaris = (side: 'left' | 'right' | 'top' | 'bottom') => [
    ...(doors || []),
    ...harisToDoors(allHaris, side, horizontal, vertical, pt, cornerExclAdjust),
  ];

  return {
    left: calculatePanelLayout(vertical - pt + leftWallExtra, config, 'left', doorsWithHaris('left')),
    right: calculatePanelLayout(vertical - pt, config, 'right', doorsWithHaris('right')),
    top: calculatePanelLayout(horizontal - pt, config, 'top', doorsWithHaris('top')),
    bottom: calculatePanelLayout(horizontal - pt, config, 'bottom', doorsWithHaris('bottom')),
  };
}

/**
 * 천장/바닥 패널 계산
 * direction 'H': 패널이 가로로 길고 세로 방향으로 분할
 * direction 'V': 패널이 세로로 길고 가로 방향으로 분할
 */
export function calculateCeilingPanels(
  horizontal: number,
  vertical: number,
  config: PanelConfig,
  direction: 'H' | 'V'
): CeilingPanel[] {
  // H: 패널 길이=horizontal, 분할 방향=vertical (세로로 900mm씩)
  // V: 패널 길이=vertical,   분할 방향=horizontal (가로로 900mm씩)
  const panelLength = direction === 'H' ? horizontal : vertical;
  const splitLength = direction === 'H' ? vertical : horizontal;

  const sizes = fillWithPanels(splitLength, config);
  const panels: CeilingPanel[] = [];
  let pos = 0;

  sizes.forEach((size, i) => {
    const isFirst = i === 0;
    const isLast = i === sizes.length - 1;
    panels.push({
      index: i,
      pos,
      size,
      length: panelLength,
      isCustom: size !== config.DEFAULT_SIZE,
      leftType: isFirst ? 'none' : 'yangsu',
      rightType: isLast ? 'none' : 'eumsu',
    });
    pos += size + config.PANEL_SPACE;
  });

  return panels;
}

/**
 * 칸막이 패널 계산
 * 양 끝은 벽에 닿으므로 none, 중간은 음양수 패턴 적용
 */
export interface MiddleWallPanelItem {
  pos: number;
  size: number;
  isCustom: boolean;
  leftType: PanelEndType;
  rightType: PanelEndType;
}

export function calculateMiddleWallPanels(
  length: number,
  config: PanelConfig,
  panelVariant: 1 | 2 | 3 | 4 = 1
): MiddleWallPanelItem[] {
  const sizes = fillWithPanels(length, config);
  const panels: MiddleWallPanelItem[] = [];
  let pos = 0;

  // panelVariant: 1=양/음, 2=음/양, 3=음/음, 4=양/양
  const startLt: PanelEndType = (panelVariant === 1 || panelVariant === 4) ? 'yangsu' : 'eumsu';
  const startRt: PanelEndType = (panelVariant === 1 || panelVariant === 3) ? 'eumsu' : 'yangsu';

  sizes.forEach((size, i) => {
    const isFirst = i === 0;
    const isLast = i === sizes.length - 1;

    let leftType: PanelEndType;
    let rightType: PanelEndType;

    if (isFirst) {
      leftType = 'none'; // 벽에 닿는 끝
      rightType = isLast ? 'none' : startRt;
    } else {
      // 이전 패널 rightType의 반대
      const prev = panels[i - 1];
      leftType = prev.rightType === 'yangsu' ? 'eumsu' : prev.rightType === 'eumsu' ? 'yangsu' : startLt;
      rightType = isLast ? 'none' : (leftType === 'yangsu' ? 'eumsu' : 'yangsu');
    }

    panels.push({ pos, size, isCustom: size !== config.DEFAULT_SIZE, leftType, rightType });
    pos += size + config.PANEL_SPACE;
  });

  return panels;
}

/**
 * 패널 수량 집계
 */
export function summarizePanels(walls: { [key: string]: Panel[] }) {
  const summary: { [size: number]: { count: number; isCustom: boolean } } = {};

  Object.values(walls).forEach(panels => {
    panels.forEach(panel => {
      if (summary[panel.size]) {
        summary[panel.size].count++;
      } else {
        summary[panel.size] = { count: 1, isCustom: panel.isCustom };
      }
    });
  });

  return Object.entries(summary)
    .map(([size, info]) => ({
      size: Number(size),
      count: info.count,
      isCustom: info.isCustom,
    }))
    .sort((a, b) => b.size - a.size);
}
